import { GridBaseProps } from '@mui/material';
import { RestrictionCheck } from '@procureai/permissions-ui';

export interface BaseModuleProps {
  moduleType: ModuleType;
  grid?: Pick<GridBaseProps, 'size'>;
  props?: Record<string, any>;
  config?: Record<string, any>;
}

export interface BaseModule extends BaseModuleProps {
  id: string;
  needs?: RestrictionCheck;
  lacks?: RestrictionCheck;
}

export enum ModuleType {
  ACTIVE_CONTRACTS_QUICKVIEW_WIDGET = 'active-contracts-quickview-widget',
  ACTIVE_CONTRACTS_WIDGET = 'active-contracts-widget',
  CONTRACT_ADHERENCE_WIDGET = 'contract-adherence-widget',
  CONTRACT_ALERTS_WIDGET = 'contract-alerts-widget',
  CONTRACT_UTILIZATION_WIDGET = 'contract-utilization-widget',
  CONTRACT_VALIDITY_WIDGET = 'contract-validity-widget',
  CONTRACTS_TOTAL_QUANTITY_WIDGET = 'contracts-total-quantity-widget',
  CONTRACTS_TOTAL_VOLUME_WIDGET = 'contracts-total-volume-widget',
  DELIVERY_RELIABILITY_WIDGET = 'delivery-reliability-widget',
  DELIVERY_RELIABILITY_QUICKVIEW_WIDGET = 'delivery-reliability-quickview-widget',
  EMISSION_COMMODITY_WIDGET = 'emission-commodity-widget',
  GENERAL_EMISSION_WIDGET = 'general-emission-widget',
  GENERAL_EMISSION_COMMODITY_WIDGET = 'general-emission-commodity-widget',
  NEWSROOM_WIDGET = 'newsroom-widget',
  MATERIAL_ORDERED_QUANTITY_WIDGET = 'material-ordered-quantity-widget',
  MATERIAL_ORDERED_VOLUME_WIDGET = 'material-ordered-volume-widget',
  SPEND_ANALYSIS_WIDGET = 'spend-analysis-widget',
  OPEN_PURCHASE_REQUISITIONS_WIDGET = 'open-purchase-requisitions-widget',
  CUBE_SAMPLE = 'cube-sample',
  DIVIDER = 'grid-divider',
  PURCHASE_ORDER_DETAILS_WIDGET = 'purchase-order-details-widget',
  PURCHASE_ORDER_EMISSION_WIDGET = 'purchase-order-emission-widget',
  CONTRACT_DETAILS_WIDGET = 'contract-details-widget',
  PURCHASE_REQUISITION_DETAILS_WIDGET = 'purchase-requisition-details-widget',
  DEMAND_DETAIL_WIDGET = 'demand-detail-widget',
  DEMAND_AWARDING_CONFIGURATION_WIDGET = 'demand-awarding-configuration-widget',
  DEMAND_RECOMMENDATION_WIDGET = 'demand-recommendation-widget',
  DEMAND_CASES_WIDGET = 'demand-cases-widget',
  DEMAND_REQUEST_WIDGET = 'demand-request-widget',
  DEMAND_AWARDING_WIDGET = 'demand-awarding-widget',
  DEMAND_AWARDING_LINE_ITEM_BIDDING_WIDGET = 'demand-awarding-line-item-bidding-widget',
  COMMODITY_SUPPLIER_LIST_WIDGET = 'commodity-supplier-list-widget',
  LARGE_VIEW_MATERIAL_WIDGET = 'large-view-material-widget',

  GENERAL_ORDER_VOLUME_COMMODITY_GROUP_WIDGET = 'general-order-volume-commodity-group-widget',
  GENERAL_DEMAND_RECOMMENDATIONS_COUNT_WIDGET = 'general-demand-recommendations-count-widget',
  GENERAL_PERCENTAGE_OF_DEMANDS_WITH_RECOMMENDATIONS_WIDGET = 'general-percentage-of-demands-with-recommendations-widget',
  BUSINESS_LOG_WIDGET = 'business-log-widget',
  // Base Commodity Group
  COMMODITY_GROUP_PURCHASE_ORDER_VOLUME_WIDGET = 'commodity-group-purchase-order-volume-widget',
  COMMODITY_GROUP_PURCHASE_ORDER_COUNT_WIDGET = 'commodity-group-purchase-order-count-widget',
  COMMODITY_GROUP_PURCHASE_ORDER_SHARE_WIDGET = 'commodity-group-purchase-order-share-widget',
  COMMODITY_GROUP_SUPPLIER_LIST_WIDGET = 'commodity-group-supplier-list-widget',
  COMMODITY_GROUP_PURCHASE_ORDER_ITEM_LIST_WIDGET = 'commodity-group-purchase-order-item-list-widget',
  // Base General
  PURCHASE_ORDER_COUNT_WIDGET = 'purchase-order-count-widget',
  PURCHASE_ORDER_VOLUME_WIDGET = 'purchase-order-volume-widget',
  // Base Supplier
  SUPPLIER_PURCHASE_ORDER_ITEM_LIST_WIDGET = 'supplier-purchase-order-item-list-widget',
  SUPPLIER_PURCHASE_ORDER_COUNT_WIDGET = 'supplier-purchase-order-count-widget',
  SUPPLIER_PURCHASE_ORDER_SHARE_WIDGET = 'supplier-purchase-order-share-widget',
  SUPPLIER_PURCHASE_ORDER_VOLUME_WIDGET = 'supplier-purchase-order-volume-widget',
  SUPPLIER_ORDER_VOLUME_COMMODITY_GROUP_WIDGET = 'supplier-order-volume-commodity-group-widget',
  SUPPLIER_EMISSION_WIDGET = 'supplier-emission-widget',
  SUPPLIER_DETAILS_WIDGET = 'supplier-details-widget',
  SUPPLIER_LOCATION_WIDGET = 'supplier-location-widget',
  SUPPLIER_CONTACT_LIST_WIDGET = 'supplier-contact-list-widget',
  // Base Company
  COMPANY_DETAILS_WIDGET = 'company-details-widget',
  // Risk
  SUPPLIER_RISK_SCORE_WIDGET = 'supplier-risk-score-widget',
  SUPPLIER_RISK_CATEGORIES_WIDGET = 'supplier-risk-categories-widget',
  SUPPLIER_RISKS = 'supplier-risks-widget',
  // Demand
  DEMAND_ATTACHMENT_WIDGET = 'demand-attachment-widget', // Old widget, to be replaced after feature flag removed
  DEMAND_FILE_ATTACHMENTS_WIDGET = 'demand-file-attachments-widget',
  // Negotiation Case
  NEGOTIATION_TERMS = 'negotiation-terms-widget',
  NEGOTIATION_SETTINGS = 'negotiation-settings-widget',
  NEGOTIATION_QUOTE_DETAILS = 'negotiation-quote-details-widget',
  NEGOTIATION_NEGOTIATED_QUOTE = 'negotiation-negotiated-quote-widget',
  NEGOTIATION_TASKS = 'negotiation-tasks',
  NEGOTIATION_SUPPLIER_CONTACT = 'negotiation-supplier-contact',
  NEGOTIATION_CONTACT = 'negotiation-contact',
  NEGOTIATION_SUPPLIER_INTRODUCTION_MESSAGE = 'negotiation-supplier-introduction-message',
  NEGOTIATION_CASE_DETAIL_STATUS = 'negotiation-case-detail-status-widget',
  NEGOTIATION_CASE_DETAIL_ISSUES = 'negotiation-case-detail-issues-widget',
  NEGOTIATION_BUYER_FILE_ATTACHMENTS = 'negotiation-buyer-file-attachments-widget',
  NEGOTIATION_SUPPLIER_FILE_ATTACHMENTS = 'negotiation-supplier-file-attachments-widget',

  // RunAI Analytics
  NEGOTIATIONS_PROCESS_METRICS_WIDGET = 'negotiations-process-metrics-widget',
  NEGOTIATIONS_SAVINGS_COMMODITY_GROUP_WIDGET = 'negotiations-savings-commodity-group-widget',
  NEGOTIATIONS_COUNT_WIDGET = 'negotiations-count-widget',
  NEGOTIATIONS_SPEND = 'negotiations-spend-widget',
  NEGOTIATIONS_AVERAGE_SAVINGS_WIDGET = 'negotiations-average-savings-widget',
  NEGOTIATIONS_TOTAL_SAVINGS_WIDGET = 'negotiations-total-savings-widget',
  // Run Ai Supplier Analytics
  SUPPLIER_NEGOTIATIONS_COUNT_WIDGET = 'supplier-negotiations-count-widget',
  SUPPLIER_NEGOTIATIONS_SPEND = 'supplier-negotiations-spend-widget',
  SUPPLIER_NEGOTIATIONS_AVERAGE_SAVINGS_WIDGET = 'supplier-negotiations-average-savings-widget',
  SUPPLIER_NEGOTIATIONS_TOTAL_SAVINGS_WIDGET = 'supplier-negotiations-total-savings-widget',
  // RISK
  RISK_MANAGED_SUPPLIER_WIDGET = 'risk-managed-supplier-widget',
  SUPPLIER_RISK_ALERT_WIDGET = 'supplier-risk-alert-widget',
  RISK_COUNTRY_REPORT_LIST_WIDGET = 'risk-country-report-list-widget',
  RISK_SUPPLIER_LIST_WIDGET = 'risk-supplier-list-widget',
  RISK_COUNTRY_REPORT_TABLE_WIDGET = 'risk-country-report-table-widget',
  RISK_COUNTRY_MAP = 'risk-country-map',

  // User
  USER_PROFILE_DETAILS_WIDGET = 'user-profile-details-widget',
  USER_PROFILE_ABSENCE_WIDGET = 'user-profile-absence-widget',
  USER_AVAILABILITY = 'user-availability-widget',

  // SOURCING PROJECTS
  SOURCING_PROJECT_INFORMATIONS_WIDGET = 'sourcing-project-informations-widget',
  SOURCING_PROJECT_SUPPLIER_WIDGET = 'sourcing-project-supplier-widget',
  SOURCING_PROJECT_REQUEST_LIST_WIDGET = 'sourcing-project-request-list-widget',

  // CONTRACT DEMANDS
  CONTRACT_DEMAND_DETAIL_WIDGET = 'contract-demand-detail-widget',
  CONTRACT_DEMAND_REQUEST_WIDGET = 'contract-demand-request-widget',
  CONTRACT_DEMAND_CASE_LIST_WIDGET = 'contract-demand-case-list-widget',
  CONTRACT_DEMAND_AWARDING_WIDGET = 'contract-demand-awarding-widget',
  CONTRACT_DEMAND_FILE_ATTACHMENTS_WIDGET = 'contract-demand-file-attachments-widget',
  SOURCING_CASE_QUOTE_WIDGET = 'sourcing-case-quote-widget',
  SOURCING_CASE_CONTACT_WIDGET = 'sourcing-case-contact-widget',
  SOURCING_CASE_NEGOTIATION_SETTINGS = 'sourcing-case-negotiation-settings',
  SOURCING_CASE_FLOW_WIDGET = 'sourcing-case-flow-widget',
  COVERAGE_ASSISTANT_WIDGET = 'coverage-assistant-widget',
  SOURCING_CASE_ISSUE_WIDGET = 'sourcing-case-issue-widget',
  SOURCING_CASE_BUYER_FILE_ATTACHMENTS_WIDGET = 'sourcing-case-buyer-file-attachments-widget',
  SOURCING_CASE_SUPPLIER_FILE_ATTACHMENTS_WIDGET = 'sourcing-case-supplier-file-attachments-widget',

  // Reminder settings
  NEGOTIATION_REMINDER_SETTINGS = 'negotiation-reminder-settings-widget',
  AUTOMATIC_ACCEPTANCE_TIMING = 'automatic-acceptance-timing-widget',
}

export enum ModuleHash {
  CONTACT = 'contact',
  QUOTE = 'quote',
  DEMAND_REQUEST = 'demand-request',
  DEMAND_DETAIL = 'demand-detail',
  CONTRACT_DEMAND_DETAIL = 'contract-demand-detail',
  SOURCING_CASE_NEGOTIATION_SETTINGS = 'sourcing-case-negotiation-settings',
}

export enum ModuleHashAction {
  EDIT = 'edit',
}

export enum ModuleSlotId {
  MAIN = 'main',
}

export type ModuleSlotMap = { [key in ModuleSlotId]?: BaseModule[] | undefined };
